import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  CheckCircle2,
  Database,
  FileArchive,
  ListChecks,
  Lock,
  LogOut,
  MessageSquareText,
  RefreshCw,
  Settings,
  ShieldCheck,
  ShoppingBag,
  Signal,
  Users,
  WalletCards,
  Webhook
} from 'lucide-react';
import './styles.css';

const nav = [
  ['Dashboard', BarChart3],
  ['Merchants', ShoppingBag],
  ['Subscriptions', WalletCards],
  ['Audit Health', ListChecks],
  ['Findings Summary', AlertTriangle],
  ['Sync/Audit Jobs', Database],
  ['Webhooks', Webhook],
  ['Exports', FileArchive],
  ['Activity', Activity],
  ['App Review Signals', Signal],
  ['Features', CheckCircle2],
  ['Settings', Settings]
] as const;

function App() {
  const [session, setSession] = useState<{ ok: boolean; user?: string | null }>({ ok: false });
  const [page, setPage] = useState<(typeof nav)[number][0]>('Dashboard');
  const [data, setData] = useState<any>({});
  const [error, setError] = useState('');

  const api = async (path: string, options: RequestInit = {}): Promise<any> => {
    const response = await fetch(path, { ...options, headers: { 'content-type': 'application/json', ...(options.headers || {}) } });
    const json = (await response.json().catch(() => ({}))) as any;
    if (!response.ok || json.ok === false) throw new Error(json.error || `HTTP ${response.status}`);
    return json;
  };

  const refreshSession = async () => {
    const result = await api('/api/session').catch(() => ({ ok: false }));
    setSession(result);
  };

  const load = async (target = page) => {
    setError('');
    try {
      const endpoint: Record<string, string> = {
        Dashboard: '/api/overview',
        Merchants: '/api/shops',
        Subscriptions: '/api/shops',
        'Audit Health': '/api/audit-health',
        'Findings Summary': '/api/findings-summary',
        'Sync/Audit Jobs': '/api/jobs',
        Webhooks: '/api/webhooks',
        Exports: '/api/exports',
        Activity: '/api/activity',
        'App Review Signals': '/api/review-signals'
      };
      if (endpoint[target]) {
        const result = await api(endpoint[target]);
        setData((prev: any) => ({ ...prev, [target]: result }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    refreshSession();
  }, []);

  useEffect(() => {
    if (session.ok) load(page);
  }, [page, session.ok]);

  if (!session.ok) return <Login onLogin={refreshSession} />;

  return (
    <div className="admin-shell">
      <aside>
        <div className="brand">
          <div className="mark">
            <ShieldCheck size={21} />
          </div>
          <div>
            <strong>StoreGuard</strong>
            <span>Owner Admin</span>
          </div>
        </div>
        <nav>
          {nav.map(([label, Icon]) => (
            <button key={label} className={page === label ? 'active' : ''} onClick={() => setPage(label)}>
              <Icon size={17} />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </aside>
      <main>
        <header>
          <div>
            <h1>{page}</h1>
            <p>Operational view with aggregate merchant data only. No raw emails, addresses, policy bodies, tokens, or webhook payloads.</p>
          </div>
          <div className="actions">
            <button title="Refresh" onClick={() => load(page)}>
              <RefreshCw size={17} />
            </button>
            <button
              onClick={async () => {
                await api('/api/logout', { method: 'POST' });
                setSession({ ok: false });
              }}
            >
              <LogOut size={17} />
              <span>Logout</span>
            </button>
          </div>
        </header>
        {error ? <div className="notice error">{error}</div> : null}
        {render(page, data[page])}
      </main>
    </div>
  );
}

function Login({ onLogin }: { onLogin: () => void }) {
  const [username, setUsername] = useState('owner');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  return (
    <div className="login">
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          setError('');
          const response = await fetch('/api/login', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ username, password }) });
          if (!response.ok) {
            setError('Invalid owner credentials');
            return;
          }
          onLogin();
        }}
      >
        <div className="mark">
          <Lock size={22} />
        </div>
        <h1>Owner admin</h1>
        <p>StoreGuard Settings Audit operational dashboard.</p>
        {error ? <div className="notice error">{error}</div> : null}
        <label>
          <span>Username</span>
          <input value={username} onChange={(event) => setUsername(event.target.value)} />
        </label>
        <label>
          <span>Password</span>
          <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
        </label>
        <button className="primary">Login</button>
      </form>
    </div>
  );
}

function render(page: string, payload: any) {
  if (page === 'Dashboard') {
    const overview = payload?.overview || {};
    return (
      <section className="stack">
        <div className="metrics">
          <Metric label="Total shops" value={overview.total_shops || 0} />
          <Metric label="Active shops" value={overview.active_shops || 0} />
          <Metric label="Paid shops" value={overview.paid_shops || 0} />
          <Metric label="Estimated MRR" value={`€${overview.estimated_mrr || 0}`} />
          <Metric label="Average score" value={overview.average_audit_score || 0} />
          <Metric label="Critical findings" value={overview.critical_findings_count || 0} tone="critical" />
          <Metric label="High findings" value={overview.high_findings_count || 0} tone="high" />
          <Metric label="Manual checks" value={overview.manual_checks_count || 0} tone="manual" />
        </div>
        <Panel title="App review signals" icon={<Signal size={18} />}>
          <Rows rows={(overview.app_review_signals || []).map((row: any) => [row.signal, row.count])} />
        </Panel>
      </section>
    );
  }
  if (page === 'Features') {
    return (
      <Panel title="Feature controls" icon={<CheckCircle2 size={18} />}>
        <Rows rows={[['Embedded app', 'Enabled'], ['Theme app', 'Not applicable'], ['Checkout extension', 'Not applicable'], ['Protected customer data', 'Not required'], ['Write scopes', 'Not requested']]} />
      </Panel>
    );
  }
  if (page === 'Settings') {
    return (
      <Panel title="Owner settings" icon={<Settings size={18} />}>
        <Rows rows={[['Auth', 'HMAC signed HttpOnly Secure SameSite=Lax cookie'], ['Data policy', 'Aggregate operational metadata only'], ['Secrets', 'Never displayed'], ['Host guard', 'Enabled for API routes']]} />
      </Panel>
    );
  }
  return (
    <Panel title={page} icon={<Users size={18} />}>
      <Rows rows={(payload?.rows || []).map((row: Record<string, unknown>) => Object.entries(row).slice(0, 8).map(([key, value]) => `${key}: ${String(value ?? '-')}`))} />
    </Panel>
  );
}

function Panel({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="panel">
      <div className="panel-title">
        {icon}
        <h2>{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Metric({ label, value, tone = '' }: { label: string; value: React.ReactNode; tone?: string }) {
  return (
    <div className={`metric ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Rows({ rows }: { rows: Array<Array<React.ReactNode>> }) {
  if (!rows.length) return <div className="empty">No rows yet.</div>;
  return (
    <div className="rows">
      {rows.map((row, index) => (
        <div key={index}>
          {row.map((cell, cellIndex) => (
            <span key={cellIndex}>{cell}</span>
          ))}
        </div>
      ))}
    </div>
  );
}

createRoot(document.getElementById('root')!).render(<App />);
